import React, { useEffect, useRef, useState } from 'react';
import { motion, useMotionValue, useSpring, useTransform } from 'framer-motion';
import NovaLogoIcon from '@/components/NovaLogoIcon';

const clamp = (value: number, min: number, max: number) => Math.min(max, Math.max(min, value));

const NovaHeroLogoStage: React.FC = () => {
  const containerRef = useRef<HTMLDivElement>(null);
  const thinkingTimeoutRef = useRef<number | null>(null);
  const [thinking, setThinking] = useState(true);

  const mouseX = useMotionValue(0);
  const mouseY = useMotionValue(0);

  const smoothX = useSpring(mouseX, { stiffness: 130, damping: 18, mass: 0.7 });
  const smoothY = useSpring(mouseY, { stiffness: 130, damping: 18, mass: 0.7 });

  const rotateY = useTransform(smoothX, [-1, 1], [-10, 10]);
  const rotateX = useTransform(smoothY, [-1, 1], [9, -9]);
  const shiftX = useTransform(smoothX, [-1, 1], [-16, 16]);
  const shiftY = useTransform(smoothY, [-1, 1], [14, -14]);

  const setThinkingPulseWindow = () => {
    setThinking(true);
    if (thinkingTimeoutRef.current !== null) {
      window.clearTimeout(thinkingTimeoutRef.current);
    }
    thinkingTimeoutRef.current = window.setTimeout(() => {
      setThinking(false);
      thinkingTimeoutRef.current = null;
    }, 1500);
  };

  useEffect(() => {
    return () => {
      if (thinkingTimeoutRef.current !== null) {
        window.clearTimeout(thinkingTimeoutRef.current);
      }
    };
  }, []);

  const handleMouseMove = (event: React.MouseEvent<HTMLDivElement>) => {
    const rect = containerRef.current?.getBoundingClientRect();
    if (!rect) return;
    const x = ((event.clientX - rect.left) / rect.width) * 2 - 1;
    const y = ((event.clientY - rect.top) / rect.height) * 2 - 1;
    mouseX.set(clamp(x, -1, 1));
    mouseY.set(clamp(y, -1, 1));
    setThinkingPulseWindow();
  };

  const handleMouseLeave = () => {
    mouseX.set(0);
    mouseY.set(0);
    setThinking(false);
  };

  return (
    <div
      ref={containerRef}
      onMouseMove={handleMouseMove}
      onMouseLeave={handleMouseLeave}
      className="relative h-full w-full cursor-default"
      style={{ perspective: 1200 }}
    >
      <div className="pointer-events-none absolute inset-0 flex items-center justify-center">
        {/* The "Aura" Ring - Perfectly sized to frame the logo */}
        <motion.div 
          animate={{ rotate: 360 }}
          transition={{ duration: 15, repeat: Infinity, ease: "linear" }}
          className="absolute w-[240px] h-[240px] md:w-[320px] md:h-[320px] lg:w-[420px] lg:h-[420px] border border-[#7F77DD]/20 rounded-full shadow-[0_0_50px_rgba(127,119,221,0.1)]"
          style={{ 
            rotateX, 
            rotateY,
            transformStyle: 'preserve-3d',
            translateZ: -20
          }}
        />
        
        {/* Subtle Background Glow */}
        <div className="absolute inset-0 bg-[radial-gradient(50%_50%_at_50%_50%,rgba(127,119,221,0.15),transparent_70%)]" />
      </div>

      <motion.div
        style={{
          rotateX,
          rotateY,
          x: shiftX,
          y: shiftY,
          transformStyle: 'preserve-3d',
        }}
        className="relative z-10 flex h-full w-full items-center justify-center"
      >
        <motion.div
          animate={{ y: [0, -10, 0] }}
          transition={{ duration: 4.2, repeat: Infinity, ease: 'easeInOut' }}
          className="relative flex items-center justify-center"
          style={{ transform: 'translateZ(40px)' }}
        >
          <div className="absolute -inset-14 rounded-full bg-[#7F77DD]/28 blur-[72px]" />
          <div className="absolute -inset-20 rounded-full bg-[#a88ff3]/18 blur-[84px]" />
          <NovaLogoIcon thinking={thinking} className="h-[220px] w-[264px] md:h-[290px] md:w-[348px] lg:h-[340px] lg:w-[408px]" />
        </motion.div>
      </motion.div>

      <motion.div
        className="pointer-events-none absolute left-[14%] top-[26%] h-2 w-2 rounded-full bg-[#7F77DD]"
        animate={{ y: [0, -9, 0], opacity: [0.35, 1, 0.35] }}
        transition={{ duration: 2.1, repeat: Infinity, ease: 'easeInOut' }}
      />
      <motion.div
        className="pointer-events-none absolute right-[17%] top-[38%] h-2.5 w-2.5 rounded-full bg-[#a88ff3]"
        animate={{ y: [0, 11, 0], opacity: [0.3, 0.9, 0.3] }}
        transition={{ duration: 2.6, repeat: Infinity, ease: 'easeInOut', delay: 0.35 }}
      />
      <motion.div
        className="pointer-events-none absolute bottom-[23%] left-[22%] h-1.5 w-1.5 rounded-full bg-[#d6b8ff]"
        animate={{ y: [0, -8, 0], opacity: [0.28, 0.8, 0.28] }}
        transition={{ duration: 2.9, repeat: Infinity, ease: 'easeInOut', delay: 0.62 }}
      />
    </div>
  );
};

export default NovaHeroLogoStage;
